import {
  useClassifierServiceGetCommonClassifierElementEndpoint,
  useClassifierServiceGetResultValueOptionEndpoint
} from 'src/api/queries'
import {
  ClassifierCategory,
  ClassifierType,
  InvestigationTestResultDto,
  TranslationDto
} from 'src/api/requests'
import { useClassifierId } from 'src/hooks'

type OptionWithTranslation = {
  id?: string
  translation?: TranslationDto
}

type Params = {
  result: InvestigationTestResultDto
  lang: keyof TranslationDto
  /** Result options configured on the result's test type. */
  resultOptions: OptionWithTranslation[]
  /** Value options of the currently selected result option. */
  valueOptions: OptionWithTranslation[]
  /** Currently selected result option id in the form. */
  resultOptionId?: string
  /** True while the configured options are still loading. */
  isOptionsLoading: boolean
}

/** Builds the Select options of the edit-result modals. A stored option that is
 *  missing from the test type's configured list (configuration changed, or
 *  saved by an older screening flow) would make the Select show its raw id, so
 *  it is appended with a label: the translation that came with the result, or
 *  the element fetched by id when it has none. The form value stays the id. */
export const useStoredResultOptions = ({
  result,
  lang,
  resultOptions,
  valueOptions,
  resultOptionId,
  isOptionsLoading
}: Params) => {
  const storedResultOptionId = result.resultOption?.id ?? undefined
  const storedValueOptionId = result.resultValueOption?.id ?? undefined

  const isResultOptionMissing =
    !!storedResultOptionId &&
    !isOptionsLoading &&
    !resultOptions.some((option) => option.id === storedResultOptionId)
  const isValueOptionMissing =
    !!storedValueOptionId &&
    !isOptionsLoading &&
    resultOptionId === storedResultOptionId &&
    !valueOptions.some((option) => option.id === storedValueOptionId)

  const resultOptionClassifierId = useClassifierId(
    ClassifierType.INVESTIGATION_RESULT_OPTION,
    ClassifierCategory.INVESTIGATIONS
  )

  const { data: fetchedResultOption } =
    useClassifierServiceGetCommonClassifierElementEndpoint(
      {
        id: String(resultOptionClassifierId),
        elementId: String(storedResultOptionId)
      },
      [resultOptionClassifierId, storedResultOptionId],
      {
        enabled:
          isResultOptionMissing &&
          !!resultOptionClassifierId &&
          !result.resultOption?.translation?.[lang]
      }
    )

  const { data: fetchedValueOption } =
    useClassifierServiceGetResultValueOptionEndpoint(
      { elementId: String(storedValueOptionId) },
      [storedValueOptionId],
      {
        enabled:
          isValueOptionMissing && !result.resultValueOption?.translation?.[lang]
      }
    )

  const toItems = (options: OptionWithTranslation[]) =>
    options.map((option) => ({
      value: option.id,
      label: option.translation?.[lang]
    }))

  const resultOptionItems = [
    ...toItems(resultOptions),
    ...(isResultOptionMissing
      ? [
          {
            value: storedResultOptionId,
            label:
              result.resultOption?.translation?.[lang] ||
              fetchedResultOption?.translation?.[lang] ||
              '-'
          }
        ]
      : [])
  ]

  const valueOptionItems = [
    ...toItems(valueOptions),
    ...(isValueOptionMissing
      ? [
          {
            value: storedValueOptionId,
            label:
              result.resultValueOption?.translation?.[lang] ||
              fetchedValueOption?.translation?.[lang] ||
              '-'
          }
        ]
      : [])
  ]

  return { resultOptionItems, valueOptionItems }
}
